import React, { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";

/**
 * AdminEventManagement Component
 * 
 * Features:
 * - Create new events with full validation
 * - Edit existing events
 * - Duplicate events for different days/times
 * - Delete events (with confirmation and cascade delete of signups)
 * - View all events in a table format
 * - Display current signup counts for each event
 * - Color-coded signup status (red when at/over capacity, green otherwise)
 * 
 * All operations include comprehensive console logging for debugging.
 */
function AdminEventManagement() {
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    speaker: "",
    time: "",
    location: "",
    max_capacity: "",
    day: "",
    category: ""
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  async function fetchEvents() {
    setLoading(true);
    
    try {
      // First, get all events
      const { data: eventsData, error: eventsError } = await supabase
        .from("events")
        .select("*")
        .order('day', { ascending: true })
        .order('time', { ascending: true });

      if (eventsError) {
        setEvents([]);
        setLoading(false);
        return;
      }

      // For each event, count signups
      const eventsWithSignups = await Promise.all(
        (eventsData || []).map(async (event) => {
          const { count, error: countError } = await supabase
            .from("event_signups")
            .select("*", { count: 'exact', head: true })
            .eq("event_id", event.event_id);

          if (countError) {
            return { ...event, signupCount: 0 };
          }
          return { ...event, signupCount: count || 0 };
        })
      );

      setEvents(eventsWithSignups);
    } catch (error) {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }

  function handleChange(e) {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  }

  async function handleSave() {
    if (!formData.title || !formData.day || !formData.category || !formData.time) {
      alert("Title, day, category, and time are required.");
      return;
    }

    const payload = {
      title: formData.title,
      description: formData.description,
      speaker: formData.speaker,
      time: formData.time,
      location: formData.location,
      max_capacity: parseInt(formData.max_capacity) || 0,
      day: formData.day,
      category: formData.category
    };

    if (editingEvent) {
      const { error } = await supabase
        .from("events")
        .update(payload)
        .eq("event_id", editingEvent.event_id)
        .select();
      if (error) {
        alert(`Error updating event: ${error.message}`);
      } else {
        alert('Event updated successfully!');
      }
    } else {
      const { error } = await supabase.from("events").insert([payload]).select();
      if (error) {
        if (error.code === '42501' || error.message.includes('RLS')) {
          alert(`RLS Policy Error: Admins need permission to insert events.\n\nRun this SQL in Supabase:\n\nCREATE POLICY "Admins can insert events" ON events\nFOR INSERT TO authenticated\nWITH CHECK (\n  EXISTS (\n    SELECT 1 FROM users\n    WHERE users.id = auth.uid()\n    AND users.role = 'admin'\n  )\n);`);
        } else {
          alert(`Error creating event: ${error.message}`);
        }
      } else {
        alert('Event created successfully! Check the Events page to see it.');
      }
    }

    setShowForm(false);
    setEditingEvent(null);
    setFormData({
      title: "",
      description: "",
      speaker: "",
      time: "",
      location: "",
      max_capacity: "",
      day: "",
      category: ""
    });
    fetchEvents();
  }

  async function handleDuplicate(event) {
    // Create clean clone without computed fields
    const { event_id, signupCount, created_at, ...eventData } = event;
    const clone = { 
      ...eventData,
      title: event.title + " (Copy)"
    };
    const { error } = await supabase.from("events").insert([clone]);
    if (!error) {
      fetchEvents();
    }
  }

  async function handleDelete(eventId) {
    // Debug: Check current user
    const { data: { user } } = await supabase.auth.getUser();

    // Debug: Check if user is admin
    await supabase
      .from('users')
      .select('role')
      .eq('id', user?.id)
      .single();

    const confirmed = window.confirm('Are you sure you want to delete this event? This will also remove all associated signups.');
    if (!confirmed) {
      return;
    }

    // First, delete associated signups
    const { error: signupError } = await supabase
      .from("event_signups")
      .delete()
      .eq("event_id", eventId);
    
    if (signupError) {
    }

    // Then delete the event
    const { data, error } = await supabase
      .from("events")
      .delete()
      .eq("event_id", eventId)
      .select();
    
    if (error) {
      alert(`Error deleting event: ${error.message}`);
    } else {
      if (data && data.length > 0) {
        alert('Event deleted successfully!');
        fetchEvents();
      } else {
        alert('Warning: Event may not have been deleted.');
      }
    }
  }

  function openEdit(event) {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      speaker: event.speaker,
      time: event.time,
      location: event.location,
      max_capacity: event.max_capacity,
      day: event.day || "",
      category: event.category || ""
    });
    setShowForm(true);
  }

  function openAdd() {
    setEditingEvent(null);
    setFormData({
      title: "",
      description: "",
      speaker: "",
      time: "",
      location: "",
      max_capacity: "",
      day: "",
      category: ""
    });
    setShowForm(true);
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-3xl font-semibold mb-6">Admin Event Management</h1>

      <button
        onClick={openAdd}
        className="mb-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
      >
        Add New Event
      </button>

      {showForm && (
        <div className="border p-4 mb-6 rounded bg-gray-50">
          <h2 className="font-semibold mb-3">
            {editingEvent ? "Edit Event" : "Create New Event"}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              name="title"
              placeholder="Event Title"
              value={formData.title}
              onChange={handleChange}
              className="border p-2 rounded"
            />
            <input
              name="speaker"
              placeholder="Speaker"
              value={formData.speaker}
              onChange={handleChange}
              className="border p-2 rounded"
            />
            <input
              name="time"
              placeholder="Time (e.g., 09:00 AM - 10:30 AM)"
              value={formData.time}
              onChange={handleChange}
              className="border p-2 rounded"
            />
            <select
              name="day"
              value={formData.day}
              onChange={handleChange}
              className="border p-2 rounded"
            >
              <option value="">Select Day</option>
              <option value="day1">Day 1 - Feb 22</option>
              <option value="day2">Day 2 - Feb 23</option>
            </select>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="border p-2 rounded"
            >
              <option value="">Select Category</option>
              <option value="keynote">Keynote</option>
              <option value="solo_talk">Solo Talk</option>
              <option value="fireside_chat">Fireside Chat</option>
              <option value="panel">Panel</option>
              <option value="engagement_event">Engagement Event</option>
            </select>
            <input
              name="location"
              placeholder="Location"
              value={formData.location}
              onChange={handleChange}
              className="border p-2 rounded"
            />
            <input
              name="max_capacity"
              type="number"
              placeholder="Max Capacity"
              value={formData.max_capacity}
              onChange={handleChange}
              className="border p-2 rounded"
            />
          </div>

          <textarea
            name="description"
            placeholder="Description"
            value={formData.description}
            onChange={handleChange}
            className="w-full border p-2 rounded mt-3"
          />

          <div className="mt-3 space-x-3">
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              {editingEvent ? "Save Changes" : "Create Event"}
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading events...</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-200 bg-white rounded-lg">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium">Title</th>
                <th className="px-4 py-2 text-left text-sm font-medium">Day</th>
                <th className="px-4 py-2 text-left text-sm font-medium">Category</th>
                <th className="px-4 py-2 text-left text-sm font-medium">Time</th>
                <th className="px-4 py-2 text-left text-sm font-medium">Location</th>
                <th className="px-4 py-2 text-left text-sm font-medium">Capacity</th>
                <th className="px-4 py-2 text-left text-sm font-medium">Signups</th>
                <th className="px-4 py-2 text-left text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {events.map((event) => (
                <tr key={event.event_id}>
                  <td className="px-4 py-2 text-sm">{event.title}</td>
                  <td className="px-4 py-2 text-sm">
                    {event.day === 'day1' ? 'Day 1 - Feb 22' : event.day === 'day2' ? 'Day 2 - Feb 23' : '-'}
                  </td>
                  <td className="px-4 py-2 text-sm">
                    {event.category || '-'}
                  </td>
                  <td className="px-4 py-2 text-sm">{event.time || "-"}</td>
                  <td className="px-4 py-2 text-sm">{event.location || "-"}</td>
                  <td className="px-4 py-2 text-sm">{event.max_capacity ?? 0}</td>
                  <td className="px-4 py-2 text-sm">
                    <span className={
                      event.signupCount >= event.max_capacity ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'
                    }>
                      {event.signupCount || 0}
                      / {event.max_capacity}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openEdit(event)}
                        className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDuplicate(event)}
                        className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                      >
                        Duplicate
                      </button>
                      <button
                        onClick={() => handleDelete(event.event_id)}
                        className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {events.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-2 text-center text-sm text-gray-500"
                  >
                    No events found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminEventManagement;
